import { useState } from "react";
import { Trash2, ArrowRight, Plus, TicketPercent, Loader2, Check } from "lucide-react";
import { QrSheet } from "../public/qr/QrSheet";
import { QrMedia } from "../public/qr/QrMedia";
import { Quantity } from "../public/qr/QrMenu";
import { money } from "./orderApi";

export const CartBar = ({ count, total, provisional = false, onOpen, aboveNav = false }) => {
  if (!count) return null;
  return (
    <div className={`os-cartbar-wrap ${aboveNav ? "above-nav" : ""}`} data-testid="cart-footer">
      <button type="button" className="os-cartbar" onClick={onOpen} data-testid="cart-bar">
        <span style={{ textAlign: "left" }}><small data-testid="cart-bar-count">{count} item{count > 1 ? "s" : ""}</small><strong data-testid="cart-bar-total" data-provisional={provisional ? "1" : "0"}>{total != null ? money(total) : "…"}{provisional && <span className="os-provisional-dot" aria-label="updating" />}</strong></span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>View Cart <ArrowRight size={18} /></span>
      </button>
    </div>
  );
};

const lineMatch = (p, l) => p.item_id === l.item_id && (p.variant_name || null) === (l.variant_name || null) && p.qty === l.qty && (p.note || "") === (l.note || "")
  && [...(p.addon_ids || p.addons?.map((a) => a.id) || [])].sort().join(",") === [...(l.addon_ids || [])].sort().join(",");

export const CartSheet = ({ open, onClose, cart, onCheckout, onAddMore }) => {
  const { lines, setQty, remove, priced, pricing, priceError, coupon, applyCoupon, estimate, confirmed } = cart;
  const show = (confirmedValue, provisionalValue) => (confirmed ? confirmedValue : provisionalValue);   // provisional amounts are flagged, never passed off as final
  const [code, setCode] = useState(coupon || "");
  if (!open) return null;
  const pl = priced?.lines || [];
  const unavailable = new Set((priced?.unavailable || []).map((u) => u.item_id));
  const applied = coupon && (priced?.discounts || []).some((d) => d.kind === "coupon");
  return (
    <QrSheet title={`Your Cart${lines.length ? ` · ${lines.reduce((s, l) => s + l.qty, 0)} item${lines.length > 1 || lines[0]?.qty > 1 ? "s" : ""}` : ""}`} id="cart-sheet" onClose={onClose}>
      <div className="qr-sheet-scroll">
        <section data-testid="cart-review">
          {lines.length === 0 && <p className="os-hint" style={{ textAlign: "center", padding: "24px 0" }} data-testid="cart-empty">Your cart is empty.</p>}
          {lines.map((l, idx) => {
            const p = pl.find((x) => lineMatch(x, l)) || pl.find((x) => x.item_id === l.item_id && (x.variant_name || null) === (l.variant_name || null) && x.qty === l.qty);
            const bad = unavailable.has(l.item_id);
            const addons = p?.addons || [];
            return (
              <article className="os-cart-line" key={idx} data-testid={`cart-line-${idx}`}>
                <span className="os-row-photo"><QrMedia src={l.image_url} alt={l.name} /></span>
                <div style={{ minWidth: 0 }}>
                  <h3>{l.name}</h3>
                  {l.variant_name && <p data-testid={`cart-line-variant-${idx}`}>{l.variant_name}</p>}
                  {addons.length > 0 && <p data-testid={`cart-line-addons-${idx}`}>{addons.map((a) => `${a.name} (+${money(a.price ?? a.unit_price ?? 0)})`).join(", ")}</p>}
                  {l.note && <p style={{ fontStyle: "italic" }}>“{l.note}”</p>}
                  {bad && <p className="os-err" data-testid={`cart-line-unavailable-${idx}`}>No longer available — remove to continue</p>}
                  <button type="button" className="os-remove" onClick={() => remove(idx)} data-testid={`cart-remove-${idx}`}><Trash2 size={13} />Remove</button>
                </div>
                <div className="os-cart-end">
                  <span data-testid={`cart-line-total-${idx}`}>{p ? money(p.line_subtotal) : money(l.unit * l.qty)}</span>
                  <Quantity id={`cart-qty-${idx}`} value={l.qty} minus={() => setQty(idx, l.qty - 1)} plus={() => setQty(idx, Math.min(20, l.qty + 1))} />
                </div>
              </article>
            );
          })}
          <button type="button" className="os-addmore" onClick={onAddMore} data-testid="cart-add-more"><Plus size={16} />Add more items</button>
        </section>

        <section data-testid="cart-summary">
          <div data-testid="cart-coupon">
            <div className="os-coupon">
              <TicketPercent size={18} style={{ color: "var(--muted)", flexShrink: 0 }} />
              <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Coupon code" maxLength={30} aria-label="Coupon code" data-testid="cart-coupon-input" />
              {coupon && coupon === code ? <button type="button" onClick={() => { applyCoupon(""); setCode(""); }} data-testid="cart-coupon-remove">Remove</button>
                : <button type="button" onClick={() => applyCoupon(code)} disabled={!code.trim() || pricing} data-testid="cart-coupon-apply">{pricing && code ? <Loader2 size={14} className="animate-spin" /> : "Apply"}</button>}
            </div>
            {applied && !pricing && <p className="os-ok" style={{ marginTop: 6 }} data-testid="cart-coupon-applied"><Check size={14} />Coupon {coupon} applied</p>}
            {priced?.coupon_error && <p className="os-err" data-testid="cart-coupon-error">{priced.coupon_error}</p>}
          </div>
          {priceError && <p className="os-err" data-testid="cart-price-error">{String(priceError)}</p>}
          <div className="os-bill">
            <div><span>Subtotal</span><b data-testid="cart-subtotal" data-provisional={confirmed ? "0" : "1"}>{show(priced && money(priced.subtotal), estimate ? money(estimate.subtotal) : "…")}</b></div>
            {(priced?.discounts || []).map((d) => <div className="disc" key={d.id} data-testid={`cart-discount-${d.kind}`}><span>{d.kind === "coupon" ? `Coupon ${d.coupon_code}` : d.name}</span><b>− {money(d.amount)}</b></div>)}
            {priced && priced.discount_total > 0 && !(priced.discounts || []).length && <div className="disc"><span>Discount</span><b data-testid="cart-discount">− {money(priced.discount_total)}</b></div>}
            {priced && priced.tax_total > 0 && <div><span>Taxes</span><b data-testid="cart-tax">{money(priced.tax_total)}</b></div>}
            <div className="total"><span>Total</span><b data-testid="cart-total" data-provisional={confirmed ? "0" : "1"}>{show(priced && money(priced.total), estimate ? <>{estimate.exact ? "" : "≈ "}{money(estimate.total)}<span className="os-provisional-dot" aria-label="updating" /></> : "…")}</b></div>
          </div>
        </section>
      </div>
      <div className="os-sheet-foot">
        <button type="button" className="os-btn primary" onClick={onCheckout} disabled={!confirmed || lines.length === 0 || unavailable.size > 0} data-testid="cart-checkout" data-confirmed={confirmed ? "1" : "0"}>
          {confirmed ? <>Continue · {money(priced.total)} <ArrowRight size={18} /></> : <>Continue · {estimate ? `${estimate.exact ? "" : "≈ "}${money(estimate.total)}` : ""} <span className="os-provisional-dot light" aria-label="confirming price" /></>}
        </button>
      </div>
    </QrSheet>
  );
};
